import * as React from "react";
import { useParams } from "react-router";

import { Box, Chip, Container, Grid, Stack, Typography } from "@mui/material";

import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import FeedIcon from "@mui/icons-material/Feed";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import VisibilityIcon from "@mui/icons-material/Visibility";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperFragment from "../../../helper-components/misc/PaperFragment.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import { formatDate, isEmpty } from "../../../helper-functions/shared/miscUtils.js";
import { getPatientDisplayName } from "../../../helper-functions/shared/patientDisplay.js";
import { queryPatient } from "../../../helper-functions/api/patientQueries.js";
import { queryAllPatientExaminations } from "../../../helper-functions/api/examinationQueries.js";

import { getLanguage } from "../../../config/language/language.js";

/**
 * A single detail line of the patient record.
 * Always rendered, so the layout stays identical when demographics are absent.
 */
const DetailLine = ({ label, value }) => (
    <Stack direction="row" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 130 }}>
            {label}
        </Typography>
        <Typography variant="body2">{value || "N/A"}</Typography>
    </Stack>
);

export default function PatientView(props) {
    const { patientId } = useParams();

    const patients = React.useMemo(() => props?.backend?.patients?.data || [], [props?.backend?.patients?.data]);
    const [patient, setPatient] = React.useState(null);
    const [examinations, setExaminations] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const id = parseInt(patientId, 10);
        const cached = patients.find((entry) => entry.id === id);

        if (!isEmpty(cached)) {
            setPatient(cached);
        } else {
            queryPatient(props, { id });
        }

        queryAllPatientExaminations(props, id, (history) => {
            setExaminations(history);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    React.useEffect(() => {
        const id = parseInt(patientId, 10);
        const cached = patients.find((entry) => entry.id === id);
        if (!isEmpty(cached)) {
            setPatient(cached);
        }
    }, [patients, patientId]);

    // Newest examination first, so the history reads top-down as a timeline.
    const sortedExaminations = React.useMemo(() => [...examinations].sort((a, b) => new Date(b.created_on) - new Date(a.created_on)), [examinations]);

    const canCompare = sortedExaminations.length >= 2;

    const handleCompare = () => {
        if (!canCompare) return;

        // Default to the two most recent examinations; both stay selectable on the comparison page.
        const followup = sortedExaminations[0].id;
        const baseline = sortedExaminations[1].id;
        props.browser.navigate(`/database/patients/${patientId}/compare?baseline=${baseline}&followup=${followup}&mode=diff`);
    };

    if (loading && !patient) {
        return (
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <PaperText value="Loading patient, please wait..." center p={8} />
            </Container>
        );
    }

    if (!patient) {
        return (
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <PaperText value={getLanguage().NO_PATIENT_FOUND} center p={8} />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm title={getLanguage().PATIENT_VIEW_TITLE} subtitle={getLanguage().PATIENT_VIEW_SUBTITLE} icon={<PermContactCalendarIcon />} />
                </Grid>

                <Grid item xs={12}>
                    <PaperFragment
                        p={2}
                        value={
                            <Stack spacing={1}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
                                    <Typography variant="h6">{getPatientDisplayName(patient)}</Typography>
                                    <Chip
                                        size="small"
                                        label={`${sortedExaminations.length} examination${sortedExaminations.length === 1 ? "" : "s"}`}
                                        color={sortedExaminations.length > 0 ? "primary" : "default"}
                                    />
                                </Stack>

                                <Box sx={{ pt: 1 }}>
                                    <DetailLine label="Patient ID" value={patient.patient_identifier} />
                                    <DetailLine label="First name" value={patient.first_name} />
                                    <DetailLine label="Last name" value={patient.last_name} />
                                    <DetailLine label="Birth date" value={patient.birth_date ? formatDate(patient.birth_date, { onlyDate: true }) : null} />
                                    <DetailLine label="Patient record #" value={String(patient.id)} />
                                    <DetailLine label="Notes" value={patient.notes} />
                                </Box>
                            </Stack>
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <PaperFragment
                        p={2}
                        value={
                            <Stack spacing={1}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Examination history
                                    </Typography>

                                    <CustomButton
                                        {...props}
                                        title="Compare examinations"
                                        variant="contained"
                                        startIcon={<CompareArrowsIcon />}
                                        onClick={handleCompare}
                                        disabled={!canCompare}
                                        tooltip={canCompare ? "Compare two examinations of this patient" : "At least two examinations are required for comparison"}
                                    />
                                </Stack>

                                {sortedExaminations.length === 0 && <Typography variant="body2">No examinations recorded for this patient yet.</Typography>}

                                {sortedExaminations.length === 1 && (
                                    <Typography variant="body2" color="text.secondary">
                                        At least two examinations are required for comparison.
                                    </Typography>
                                )}

                                {sortedExaminations.map((exam) => (
                                    <Stack
                                        key={exam.id}
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        alignItems={{ sm: "center" }}
                                        sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                            {formatDate(exam.created_on)} | Examiner: {exam.examiner?.username || "N/A"} | Record #{exam.id}
                                        </Typography>

                                        <CustomButton
                                            {...props}
                                            title="View examination"
                                            size="small"
                                            variant="outlined"
                                            startIcon={<VisibilityIcon />}
                                            path={`/database/examinations/${exam.id}/view`}
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        }
                    />
                </Grid>

                <NavigationFooter
                    rows={1}
                    options={[
                        {
                            path: `/database/patients`,
                            title: "Back to patients",
                            variant: "outlined",
                            icon: <PermContactCalendarIcon />
                        },
                        {
                            path: `/database/examinations/new`,
                            title: "New examination",
                            variant: "contained",
                            icon: <FeedIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
